import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { paymentService } from 'services/paymentService';
import { CreateInvoiceDto, PaymentPaginatedResponse, SessionPayment, UpdateSessionPaymentDto } from 'types/models';

const KEY      = 'payments';
const INV_KEY  = 'invoices';

interface PaymentsAllParams {
  estadoPago?: string;
  desde?: string;
  hasta?: string;
}

export function usePaymentsAll(params?: PaymentsAllParams) {
  return useQuery<PaymentPaginatedResponse, Error, SessionPayment[]>({
    queryKey: [KEY, 'all', params],
    queryFn: () => paymentService.getAll(params),
    select: (res) => res.data,
  });
}

export function usePaymentsByPatient(patientId: string) {
  return useQuery<PaymentPaginatedResponse, Error, SessionPayment[]>({
    queryKey: [KEY, 'patient', patientId],
    queryFn:  () => paymentService.getByPatient(patientId),
    enabled:  !!patientId,
    select:   (res) => res.data,
  });
}

export function useUpdatePayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSessionPaymentDto }) =>
      paymentService.update(id, payload),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

export function useInvoicesByPayment(paymentId: string) {
  return useQuery({
    queryKey: [INV_KEY, paymentId],
    queryFn:  () => paymentService.getInvoices(paymentId),
    enabled:  !!paymentId,
  });
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ paymentId, payload }: { paymentId: string; payload: CreateInvoiceDto }) =>
      paymentService.createInvoice(paymentId, payload),
    onSuccess: (_data, { paymentId }) => {
      queryClient.invalidateQueries({ queryKey: [INV_KEY, paymentId] });
      queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}
